'use client'
import Image from 'next/image'
import { useRef } from 'react'
import copy from '../_content/site-copy.json'
import type { Artifact } from '../_lib/data'
function transformBetween(from: DOMRect, to: DOMRect) {
  return `translate(${from.x + from.width / 2 - to.x - to.width / 2}px, ${from.y + from.height / 2 - to.y - to.height / 2}px) scale(${from.width / to.width})`
}
const timing = {
  duration: 380,
  easing: 'cubic-bezier(.22,1,.36,1)',
  fill: 'both' as const,
}
export function ItemViewer({
  image,
  title,
}: {
  image: Artifact['image']
  title: string
}) {
  const dialog = useRef<HTMLDialogElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const expanded = useRef<HTMLDivElement>(null)
  const closing = useRef(false)
  const containedBounds = (element: HTMLElement) => {
    const box = element.getBoundingClientRect()
    const scale = Math.min(box.width / image.width, box.height / image.height)
    const width = image.width * scale
    const height = image.height * scale
    return new DOMRect(
      box.x + (box.width - width) / 2,
      box.y + (box.height - height) / 2,
      width,
      height,
    )
  }
  const open = () => {
    if (!dialog.current || !trigger.current || !expanded.current) return
    const origin = containedBounds(trigger.current.querySelector('img')!)
    delete dialog.current.dataset.closing
    dialog.current.showModal()
    const scale = Math.min(
      (window.innerWidth * 0.85) / image.width,
      (window.innerHeight * 0.75) / image.height,
    )
    expanded.current.style.width = `${image.width * scale}px`
    expanded.current.style.height = `${image.height * scale}px`
    const destination = expanded.current.getBoundingClientRect()
    expanded.current.animate(
      [
        { transform: transformBetween(origin, destination) },
        { transform: 'none' },
      ],
      timing,
    )
  }
  const close = () => {
    if (!expanded.current || !trigger.current || closing.current) return
    closing.current = true
    if (dialog.current) dialog.current.dataset.closing = 'true'
    const currentTransform = getComputedStyle(expanded.current).transform
    expanded.current.getAnimations().forEach((animation) => animation.cancel())
    const origin = containedBounds(trigger.current.querySelector('img')!)
    const destination = expanded.current.getBoundingClientRect()
    const animation = expanded.current.animate(
      [
        { transform: currentTransform },
        { transform: transformBetween(origin, destination) },
      ],
      timing,
    )
    const finish = () => {
      dialog.current?.close()
      closing.current = false
      trigger.current?.focus({ preventScroll: true })
    }
    void animation.finished.then(finish, finish)
  }
  return (
    <>
      <button
        className="museum-item-image-button"
        type="button"
        onClick={open}
        ref={trigger}
        aria-label={title}
      >
        <Image
          src={image.src}
          alt={title}
          width={image.width}
          height={image.height}
          sizes="(max-width: 1023px) 85vw, 45vw"
          priority
        />
      </button>
      <dialog
        className="museum-viewer"
        aria-label={title}
        ref={dialog}
        data-lenis-prevent
        onCancel={(event) => {
          event.preventDefault()
          close()
        }}
      >
        <button
          className="museum-viewer-backdrop"
          data-press="none"
          type="button"
          tabIndex={-1}
          onClick={close}
          aria-label={copy.notFound.back}
        />
        <button
          className="museum-viewer-close"
          type="button"
          onClick={close}
          aria-label={copy.notFound.back}
        >
          ×
        </button>
        <div className="museum-viewer-image" ref={expanded}>
          <Image
            src={image.src}
            alt={title}
            width={image.width}
            height={image.height}
            sizes="90vw"
          />
        </div>
        <p>{title}</p>
      </dialog>
    </>
  )
}
